import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  InputSignal,
  OnInit,
  OutputEmitterRef,
  Signal,
  WritableSignal,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { ReactiveMap, TypedTemplateDirective } from '@core/shared/util';
import { AutocompleteComponent, SelectOption } from '@core/ui';
import {
  PlattformPackungseinheit,
  PlattformPackungseinheitMapping,
} from '@mp/content-manager/industriekataloge/data-access';

@Component({
  selector: 'mp-packungseinheiten-selector',
  templateUrl: './packungseinheiten-selector.component.html',
  styleUrl: './packungseinheiten-selector.component.scss',
  imports: [AutocompleteComponent, TypedTemplateDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PackungseinheitenSelectorComponent implements OnInit {
  @HostBinding() readonly class = 'mp-packungseinheiten-selector';

  readonly packagingUnits: InputSignal<PlattformPackungseinheit[]> = input<PlattformPackungseinheit[]>([]);
  readonly value: InputSignal<PlattformPackungseinheitMapping | undefined> = input<PlattformPackungseinheitMapping>();
  readonly cache: InputSignal<ReactiveMap<string, string | undefined> | undefined> =
    input<ReactiveMap<string, string | undefined>>();

  readonly emitChanged: OutputEmitterRef<PlattformPackungseinheit | null> = output<PlattformPackungseinheit | null>();

  protected readonly selectedValue: WritableSignal<PlattformPackungseinheit | undefined> = signal<
    PlattformPackungseinheit | undefined
  >(undefined);

  protected readonly searchTerm: WritableSignal<string> = signal<string>('');

  protected readonly filteredOptions: Signal<SelectOption<PlattformPackungseinheit>[]> = computed<
    SelectOption<PlattformPackungseinheit>[]
  >(() => this.getFilteredOptions());

  protected readonly unitAutocomplete: Signal<AutocompleteComponent<PlattformPackungseinheit>> =
    viewChild.required(AutocompleteComponent);

  ngOnInit(): void {
    const cache: ReactiveMap<string, string | undefined> | undefined = this.cache();
    const value: PlattformPackungseinheitMapping | undefined = this.value();

    if (cache && value?.shortName) {
      const cachedValue = cache.get(value.shortName);

      if (cachedValue) {
        const cachedOption = this.packagingUnits().find(({ shortName }) => shortName === cachedValue);
        this.selectedValue.set(cachedOption ?? undefined);
      }
    }
  }

  private getFilteredOptions(): SelectOption<PlattformPackungseinheit>[] {
    const filteredPackagingUnits: PlattformPackungseinheit[] = this.filterPackagingUnitsBySearchTerm(this.searchTerm());
    return this.mapPackagingUnitsToSelectOptions(filteredPackagingUnits);
  }

  private mapPackagingUnitsToSelectOptions(
    packagingUnits: PlattformPackungseinheit[],
  ): SelectOption<PlattformPackungseinheit>[] {
    return packagingUnits.map((packagingUnit) => ({
      value: packagingUnit,
      label: this.getPackagingUnitLabel(packagingUnit),
    }));
  }

  private getPackagingUnitLabel(packagingUnit: PlattformPackungseinheit): string {
    if (packagingUnit.shortName && packagingUnit.longName) {
      return `${packagingUnit.shortName} (${packagingUnit.longName})`;
    } else {
      return '';
    }
  }

  private filterPackagingUnitsBySearchTerm(searchTerm: string): PlattformPackungseinheit[] {
    if (!searchTerm) {
      return this.packagingUnits();
    }

    const filterValue = searchTerm.toLowerCase();

    return this.packagingUnits().filter((option: PlattformPackungseinheit) => {
      const optionString = option.shortName + ' (' + option.longName + ')';

      return optionString.toLowerCase().includes(filterValue);
    });
  }

  protected onSelectChange(selectedPackagingUnit: PlattformPackungseinheit | undefined): void {
    this.emitChanged.emit(selectedPackagingUnit ?? null);
  }

  protected onSearchTermChange(searchTerm: string): void {
    this.searchTerm.set(searchTerm);
  }

  public focus(): void {
    this.unitAutocomplete().focus();
  }
}
